import {Component, Prop, Vue} from "vue-property-decorator";
import _, {forEach} from "lodash";
import "@/lib/vue-treeselect/dist/vue-treeselect.css";
import {Getter} from "vuex-class";
import Owner from "@/model/cospirit-api/Owner";

export interface OptionOwner {
    uuid: string;
    fullName: string;
}

export interface Node {
    id: string;
    uuid: string;
    label: string;
    value: string;
    children?: Node[];
    parent?: {
        uuid: string;
    } | null;
}

@Component
export default class CommonSelectOwnerMixin extends Vue {

    @Getter("getMasterOwners") protected masterOwners!: Owner[];
    @Prop(Array) protected ownerList!: OptionOwner[];
    protected availableOwners: OptionOwner[] = [];
    protected filteredOwners: Owner[] = [];
    protected children: { [key: string]: Owner } = {};
    protected parents: { [key: string]: Owner } = {};
    protected filteredOwnersUuids: string[] = [];
    protected defaultExpandLevel: number = 1;
    private allNodesByUuid: { [key: string]: Node } = {};


    protected get treeOptions(): Node[] {
        const nodes: Node[] = [];
        const parentsNodes: { [key: string]: Node } = {};

        this.allNodesByUuid = {};

        const owners = this.availableOwners.map((o) => o.uuid);

        if (!this.filteredOwners.length) {
            this.filteredOwners = _.clone(this.masterOwners);
        }

        this.filteredOwnersUuids = this.filteredOwners.map((o) => o.uuid);

        _.forEach(this.filteredOwners, (owner: Owner) => {
            if (!owners.includes(owner.uuid)) {
                return;
            }

            const node = this.createOrGetNode(owner);

            if (parentsNodes[owner.uuid]) {
                return;
            }

            this.findOrCreateParent(node, nodes, parentsNodes);
        });

        this.defaultExpandLevel = nodes.length > 1 ? 0 : 1;

        return this.cleanEmptyChildren(nodes);
    }

    private cleanEmptyChildren(nodes: Node[]): Node[] {
        return nodes.map((node) => {
            if (node.children && node.children.length > 0) {
                node.children = this.cleanEmptyChildren(node.children);
            } else {
                delete node.children;
            }
            return node;
        });
    }

    private createOrGetNode(owner: Owner): Node {
        if (this.allNodesByUuid[owner.uuid]) {
            return this.allNodesByUuid[owner.uuid];
        }

        const node: Node = {
            id: owner.uuid,
            uuid: owner.uuid,
            label: this.generateFullName(owner),
            value: owner.uuid,
            parent: owner.parent,
            children: []
        };

        this.allNodesByUuid[owner.uuid] = node;
        return node;
    }

    protected findOrCreateParent(
        node: Node,
        nodes: Node[],
        parentsNodes: { [key: string]: Node }
    ) {
        const parentUuid = node.parent?.uuid;

        if (!parentUuid || !this.filteredOwnersUuids.includes(parentUuid)) {
            if (!nodes.find((n) => n.uuid === node.uuid)) {
                nodes.push(node);
            }
            return;
        }

        const parentOwner = this.parents[parentUuid];

        if (!parentOwner) {
            if (!nodes.find((n) => n.uuid === node.uuid)) {
                nodes.push(node);
            }
            return;
        }

        let parentNode = parentsNodes[parentUuid];

        if (!parentNode) {
            parentNode = this.createOrGetNode(parentOwner);
            parentsNodes[parentUuid] = parentNode;

            this.findOrCreateParent(parentNode, nodes, parentsNodes);
        }

        if (!parentNode.children) {
            parentNode.children = [];
        }

        if (!parentNode.children.find((c) => c.uuid === node.uuid)) {
            parentNode.children.push(node);
        }
    }

    protected inNodes(uuid: string, nodes: Node[]): boolean {
        for (const node of nodes) {
            if (node.uuid === uuid) {
                return true;
            }
            if (node.children && this.inNodes(uuid, node.children)) {
                return true;
            }
        }

        return false;
    }

    protected generateFullName(owner: Owner): string {
        let fullName = _.get(owner, "fullName", "");

        if (owner.clientCode1) {
            fullName = fullName + " - " + owner.clientCode1;
        }

        if (owner.address && owner.address.city) {
            fullName = fullName + " (" + owner.address.city + ")";
        }

        return fullName;
    }

    protected addChildrenAndParents(owner: Owner) {
        this.children[owner.uuid] = owner;
        if (owner.parent && owner.parent.uuid) {
            const uuid = owner.parent.uuid;
            const child = this.children[uuid];
            if (child) {
                this.parents[uuid] = child;
                delete this.children[uuid];
            }
        }
    }
}
